import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Badge, Spinner, 
  Alert, Modal, Form, InputGroup, 
  Tab, Nav, Card, Row, Col 
} from 'react-bootstrap';
import { 
  FaPlus, FaEdit, FaTrash, FaClock,
  FaCalendarAlt, FaPercentage, FaToggleOn, FaToggleOff
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const LateNightCharges = () => {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentCharge, setCurrentCharge] = useState(null);
  const [activeTab, setActiveTab] = useState('recurring');

  // Form state
  const [formData, setFormData] = useState({
    isRecurring: true,
    date: '',
    startTime: '00:00',
    endTime: '04:00',
    percentageIncrease: 20,
    isEnabled: true
  });

  const fetchCharges = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/late-night-charges`, {
        headers: { 'x-auth-token': token }
      });
      setCharges(response.data);
    } catch (err) {
      console.error('Error fetching charges:', err);
      setError(err.response?.data?.error || 'Failed to load charges');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCharges();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      
      if (currentCharge) {
        // Update existing
        await axios.put(`${process.env.REACT_APP_API_URL}/admin/late-night-charges/${currentCharge._id}`,
          formData,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Charge updated successfully');
      } else {
        // Create new
        await axios.post(`${process.env.REACT_APP_API_URL}/admin/late-night-charges`,
          formData,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Charge added successfully');
      }
      
      setShowModal(false);
      fetchCharges();
    } catch (err) {
      console.error('Error saving charge:', err);
      setError(err.response?.data?.error || 'Failed to save charge');
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/late-night-charges/${currentCharge._id}`,
        { headers: { 'x-auth-token': token } }
      );
      toast.success('Charge deleted successfully');
      setShowDeleteModal(false);
      fetchCharges();
    } catch (err) {
      console.error('Error deleting charge:', err);
      setError(err.response?.data?.error || 'Failed to delete charge');
    }
  };

  const openEditModal = (charge) => {
    setCurrentCharge(charge);
    setFormData({
      isRecurring: charge.isRecurring,
      date: charge.date || '',
      startTime: charge.startTime,
      endTime: charge.endTime,
      percentageIncrease: charge.percentageIncrease,
      isEnabled: charge.isEnabled
    });
    setShowModal(true);
  };

  const openNewModal = (isRecurring) => {
    setCurrentCharge(null);
    setFormData({
      isRecurring,
      date: '',
      startTime: '00:00',
      endTime: '04:00',
      percentageIncrease: 20,
      isEnabled: true
    });
    setShowModal(true);
  };

  const toggleStatus = async (charge) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${process.env.REACT_APP_API_URL}/admin/late-night-charges/${charge._id}`,
        { isEnabled: !charge.isEnabled },
        { headers: { 'x-auth-token': token } }
      );
      toast.success('Status updated successfully');
      fetchCharges();
    } catch (err) {
      console.error('Error toggling status:', err);
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const renderChargeRow = (charge) => (
    <tr key={charge._id}>
      <td>
        {charge.isRecurring ? (
          <Badge bg="primary">Daily</Badge>
        ) : (
          <>
            <FaCalendarAlt className="me-2" />
            {new Date(charge.date).toLocaleDateString()}
          </>
        )}
      </td>
      <td>
        <FaClock className="me-2" />
        {charge.startTime} - {charge.endTime}
      </td>
      <td>
        <FaPercentage className="me-2" />
        {charge.percentageIncrease}%
      </td>
      <td>
        <Button
          variant={charge.isEnabled ? 'success' : 'secondary'}
          size="sm"
          onClick={() => toggleStatus(charge)}
        >
          {charge.isEnabled ? <FaToggleOn /> : <FaToggleOff />}
          {charge.isEnabled ? ' Enabled' : ' Disabled'}
        </Button>
      </td>
      <td>
        <Button
          variant="primary"
          size="sm"
          className="me-2"
          onClick={() => openEditModal(charge)}
        >
          <FaEdit />
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={() => {
            setCurrentCharge(charge);
            setShowDeleteModal(true);
          }}
        >
          <FaTrash />
        </Button>
      </td>
    </tr>
  );

  return (
    <div className="late-night-charges-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Late Night Charges Management</h2>
        <div>
          <Button 
            variant="primary" 
            className="me-2"
            onClick={() => openNewModal(true)}
          >
            <FaPlus className="me-2" />
            Add Recurring Charge
          </Button>
          <Button 
            variant="info"
            onClick={() => openNewModal(false)}
          >
            <FaPlus className="me-2" />
            Add Date-Specific Charge
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="recurring">Recurring Charges</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="specific">Date-Specific Charges</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="recurring">
            <Card>
              <Card.Body>
                {loading ? (
                  <div className="text-center my-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Time Range</th>
                        <th>Increase</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {charges
                        .filter(charge => charge.isRecurring)
                        .map(renderChargeRow)}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>
          <Tab.Pane eventKey="specific">
            <Card>
              <Card.Body>
                {loading ? (
                  <div className="text-center my-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Time Range</th>
                        <th>Increase</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {charges
                        .filter(charge => !charge.isRecurring)
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map(renderChargeRow)}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentCharge ? 'Edit Charge' : 'Add New Charge'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Charge Type</Form.Label>
                  <Form.Select
                    name="isRecurring"
                    value={formData.isRecurring}
                    onChange={handleInputChange}
                    disabled={!!currentCharge}
                  >
                    <option value={true}>Recurring (Daily)</option>
                    <option value={false}>Date-Specific</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {!formData.isRecurring && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required={!formData.isRecurring}
                      disabled={!!currentCharge}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Percentage Increase</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      name="percentageIncrease"
                      value={formData.percentageIncrease}
                      onChange={handleInputChange}
                      min="1"
                      max="100"
                      required
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Check
                    type="switch"
                    name="isEnabled"
                    label={formData.isEnabled ? 'Enabled' : 'Disabled'}
                    checked={formData.isEnabled}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {currentCharge ? 'Update Charge' : 'Add Charge'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this charge? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LateNightCharges;